import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid
} from "@mui/material";
import { FilterList, GridViewOutlined, ViewTimelineOutlined } from "@mui/icons-material";

import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import { PaginationControls, SearchField, DrawerControl, FilterSideBar } from "../../../shared/components";

import { useNavigation } from "../../../hooks/useNavigation";

import { ProjectCard, useFetchProjects } from "../index";
import { Phase } from "../types";
import { ProjectsGanttView, ZoomLevel } from "./ProjectsGanttView";

export const AllProjects: React.FC<{ isPinned: boolean }> = ({ isPinned }) => {

  const { portfolioId, portfolioName } = useParams();

  const {
    isLoading, isError, projects, pagination, searchDraft, projectStages, status, isOpenFilter,
    setStatus, handleChangeSearch, handleClear, handleSearch, changePage, changeRowsPerPage,
    handleOpenFilter, handleCloseFilter, handlePinProject
  } = useFetchProjects(isPinned, portfolioId);

  const { goToProjectDetails, goToProjectDetailsFromPortfolio } = useNavigation();
  const [searchParams, setSearchParams] = useSearchParams();

  const viewParam = searchParams.get("view");
  const [alignment, setAlignment] = useState<string | null>(() => {
    return viewParam === "gantt" ? "center" : "left";
  });
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>("month");

  useEffect(() => {
    const currentView = viewParam === "gantt" ? "center" : "left";
    if (alignment !== currentView) {
      setAlignment(currentView);
    }
  }, [viewParam]);

  const handleAlignment = (
    _e: React.MouseEvent<HTMLElement>,
    newAlignment: string | null
  ) => {
    if (newAlignment === null) return;
    setAlignment(newAlignment);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (newAlignment === "center") {
        newParams.set("view", "gantt");
      } else {
        newParams.delete("view");
      }
      return newParams;
    });
  };

  const handleZoomChange = (
    _e: React.MouseEvent<HTMLElement>,
    newZoom: ZoomLevel | null
  ) => {
    if (newZoom) setZoomLevel(newZoom);
  };

  const handleNavigate = (project: { id: string | number, title: string }) => {
    if (portfolioId && portfolioName && portfolioId) {
      goToProjectDetailsFromPortfolio(portfolioName, portfolioId, String(project.id), String(project.title));
    } else {
      goToProjectDetails(Number(project.id), String(project.title));
    }
  };

  if (isLoading) {
    return (
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        gap={2}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
      >
        <Alert
          severity="warning"
          sx={{
            width: "auto",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          {isError}
        </Alert>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" height="100%" gap={1.5}>

      <Box display="flex" alignItems="center" gap={2} px={2}>

        <ToggleButtonGroup
          value={alignment}
          exclusive
          onChange={handleAlignment}
          aria-label="view toggle"
          size="small"
        >
          <ToggleButton value="left" aria-label="grid view">
            <GridViewOutlined sx={{ color: "text.secondary" }} />
          </ToggleButton>
          <ToggleButton value="center" aria-label="list view">
            <ViewTimelineOutlined sx={{ color: "text.secondary" }} />
          </ToggleButton>
        </ToggleButtonGroup>

        <SearchField
          label="Project Reference, Title or Address"
          searchTerm={searchDraft}
          handleSearch={handleChangeSearch}
          handleClearSearch={handleClear}
          handleApplySearch={handleSearch}
        />

        <Button
          startIcon={<FilterList />}
          variant="text"
          color="inherit"
          onClick={handleOpenFilter}
        >
          FILTERS
        </Button>

        {alignment === "center" && (
          <ToggleButtonGroup
            value={zoomLevel}
            exclusive
            onChange={handleZoomChange}
            size="small"
            sx={{ ml: "auto" }}
          >
            <ToggleButton value="day">Day</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
            <ToggleButton value="month">Month</ToggleButton>
            <ToggleButton value="year">Year</ToggleButton>
          </ToggleButtonGroup>
        )}

      </Box>

      <Box display="flex" gap={1} flexWrap="wrap" px={2}>
        {[...projectStages].sort((a, b) => a.sequence_order - b.sequence_order).map((stage, index) => (
          <Chip
            disabled={isLoading}
            variant={"filled"}
            color={(stage.id === status.id && stage.name === status.name) ? "primary" : "default"}
            key={index}
            label={`${stage.name} (${stage.project_count})`}
            onClick={() => {
              changePage(1);
              setStatus({ id: stage.id, name: stage.name });
            }}
          />
        ))}
      </Box>

      <Box flex={1} overflow="auto" px={alignment === "center" ? 0 : 2} pb={alignment === "center" ? 0 : 2}>
        {projects.length === 0 ? (
          <Box
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
          >
            <Alert
              severity="warning"
              sx={{
                width: "auto",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              No projects found. You can add a new project to get started.
            </Alert>
          </Box>
        ) : alignment === "center" ? (
          <ProjectsGanttView
            projects={projects}
            zoomLevel={zoomLevel}
            onProjectClick={(projectId: number, projectName: string) => { goToProjectDetails(projectId, projectName) }}
          />
        ) : (
          <Grid
            container
            spacing={2}
            justifyContent="start"
            alignItems="stretch"
            alignContent="stretch"
          >
            {projects.map((project: { title: string; address: string; workstream: string; phase_name: string; project_reference: string; phases: Phase[]; progress: { percentage: number; }; id: string | number; rag: { time: "green" | "red" | "amber"; cost: "green" | "red" | "amber"; quality: "green" | "red" | "amber"; overall: "green" | "red" | "amber"; }; is_fav: boolean; planned_start_date?: string | null; planned_end_date?: string | null; }, index: React.Key | null | undefined) => (
              <Grid key={index} size={{ sm: 12, xs: 12, md: 3, lg: 3 }}>
                <ProjectCard
                  title={project.title}
                  location={project.address}
                  workType={project.workstream}
                  approvalStage={project.phase_name}
                  projectId={project.project_reference}
                  phases={project.phases}
                  progress={project.progress.percentage}
                  handleClick={() => handleNavigate(project)}
                  ragStatus={project.rag}
                  isPinned={project.is_fav}
                  handlePinToggle={async (pin: boolean) => {
                    await handlePinProject(String(project.id), pin);
                  }}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <PaginationControls
        total={pagination.total}
        rowsPerPage={pagination.limit}
        currentPage={pagination.page}
        handlePageChange={changePage}
        handleRowsPerPage={changeRowsPerPage}
      />

      <DrawerControl
        title="Filter Records"
        subTitle="Please apply following filters to refine results"
        isOpen={isOpenFilter}
        handleClose={handleCloseFilter}
      >
        <FilterSideBar onClose={handleCloseFilter} />
      </DrawerControl>

    </Box>
  );
};
